// =============================================================================
// LIL & CO — Rétention email (helpers purs, sans I/O)
// =============================================================================
// Alimente la vue super admin « Rétention email » : rappels NON-PROMOTIONNELS
// envoyés aux clients d'un resto (par défaut Big Burger) pour les inciter à
// repasser par le site. Ces helpers agrègent le journal `retention_email_log`
// et calculent la prochaine vague (cron bimensuel : 1er et 15 du mois).
// =============================================================================

export interface RetentionLogRow {
  id: string;
  tenant_id: string | null;
  customer_id: string | null;
  email: string | null;
  variant_key: string | null;
  status: string | null; // 'sent' | 'error'
  error_message: string | null;
  sent_at: string; // ISO
}

/** Libellés lisibles des variantes (clé technique -> humain). */
export const VARIANT_LABELS: Record<string, string> = {
  praticite: 'Praticité (commander à l’avance)',
  file_attente: 'Sans file d’attente',
  heure_retrait: 'Heure de retrait au choix',
  paiement: 'Paiement en ligne',
  fidelite: 'Fidélité',
  first_order: 'Première commande (non-acheteurs)',
};

export const variantLabel = (key: string | null): string =>
  (key && VARIANT_LABELS[key]) || key || '—';

/** Les audiences déclenchables (côté Edge Function send-retention-email). */
export type RetentionAudience = 'past_customers' | 'never_ordered' | 'consent';

export interface AudienceMeta {
  key: RetentionAudience;
  label: string;
  hint: string;
}

export const AUDIENCES: AudienceMeta[] = [
  { key: 'past_customers', label: 'Ont déjà commandé', hint: '≥ 1 commande en ligne — rappel de repasser par le site' },
  { key: 'never_ordered', label: 'Jamais commandé', hint: 'compte créé / commande débutée sans finaliser — « première commande »' },
  { key: 'consent', label: 'Consentement explicite', hint: 'marketing_consent = true uniquement' },
];

export interface VariantStat {
  key: string;
  label: string;
  sent: number;
  errors: number;
}

export interface RetentionStats {
  totalSent: number;
  totalErrors: number;
  uniqueRecipients: number;
  lastWaveAt: string | null;
  byVariant: VariantStat[];
}

/** Agrège le journal en statistiques d'ensemble. */
export function retentionStats(rows: RetentionLogRow[]): RetentionStats {
  const byVariant = new Map<string, VariantStat>();
  const recipients = new Set<string>();
  let totalSent = 0;
  let totalErrors = 0;
  let lastWaveAt: string | null = null;

  for (const r of rows) {
    const key = r.variant_key || '—';
    const vs = byVariant.get(key) ?? { key, label: variantLabel(key), sent: 0, errors: 0 };
    if (r.status === 'sent') {
      vs.sent += 1;
      totalSent += 1;
      if (r.customer_id) recipients.add(r.customer_id);
      else if (r.email) recipients.add(r.email.toLowerCase());
    } else {
      vs.errors += 1;
      totalErrors += 1;
    }
    byVariant.set(key, vs);
    if (!lastWaveAt || r.sent_at > lastWaveAt) lastWaveAt = r.sent_at;
  }

  return {
    totalSent,
    totalErrors,
    uniqueRecipients: recipients.size,
    lastWaveAt,
    byVariant: [...byVariant.values()].sort((a, b) => b.sent - a.sent),
  };
}

/** Regroupe les envois par jour (YYYY-MM-DD) pour l'historique des vagues. */
export interface WaveDay {
  day: string; // YYYY-MM-DD
  sent: number;
  errors: number;
}

export function wavesByDay(rows: RetentionLogRow[]): WaveDay[] {
  const map = new Map<string, WaveDay>();
  for (const r of rows) {
    const day = (r.sent_at || '').slice(0, 10);
    if (!day) continue;
    const w = map.get(day) ?? { day, sent: 0, errors: 0 };
    if (r.status === 'sent') w.sent += 1; else w.errors += 1;
    map.set(day, w);
  }
  return [...map.values()].sort((a, b) => (a.day < b.day ? 1 : -1));
}

/**
 * Prochaine vague automatique : le cron tourne les 1er et 15 de chaque mois.
 * Renvoie la prochaine date (>= maintenant) au format ISO (minuit local).
 */
export function nextWaveDate(now: Date = new Date()): Date {
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  if (d < 15) return new Date(y, m, 15);
  if (d >= 15) return new Date(y, m + 1, 1);
  return new Date(y, m, 15);
}
